import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CONFIG, IMAGE_DETECTOR, IV_THEME_MODES, SELECTORS, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { FormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { EngineService } from '@core/services/engine/engine.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { ModalService } from '@core/services/modal/modal.service';
import { ArLoadingModalComponent } from '@shared/modals/ar-loading-modal/ar-loading-modal.component';
import { RotateDeviceModalComponent } from '@shared/modals/rotate-device-modal/rotate-device-modal.component';
import {
	BackgroundConfig,
	BlindData,
	CropperSettings,
	CropperAction,
	ProjectSamplesImages,
	LoadedImageData,
	PhotoForm,
} from '@root/app.interfaces';
import { IndexedDBService } from '@core/services/indexed-db/indexed-db.service';
import { DetectionService } from '@core/services/detection/detection.service';
import { SlickCarouselComponent } from 'ngx-slick-carousel';
import Cropper from 'cropperjs';
import * as _ from 'lodash';
import { first } from 'rxjs/operators';
import CropEndEvent = Cropper.CropEndEvent;

@Component({
	selector: 'app-select-background-modal',
	templateUrl: './select-background-modal.component.html',
	styleUrls: ['./select-background-modal.component.scss', './select-background-modal.responsive.scss'],
})
export class SelectBackgroundModalComponent implements OnInit, OnDestroy {
	static readonly ID = crypto.randomUUID();

	@ViewChild('formElement') formElement: ElementRef;
	@ViewChildren('slickModal') slickModal: QueryList<SlickCarouselComponent>;

	isMobile: boolean = this.passDataService.isMobileOrTablet;
	isImageVisualisation: boolean;
	isLoading = false;
	isSubmitting = false;
	getBlindData: BlindData[] | null;
	getProductType: string;
	selectedBackgroundType = '';
	selectedProductType = '';
	allowedMimeType: string[] = ['image/png', 'image/jpeg', 'image/jpg'];
	maxFileSize = 10000000;
	maxSizeError = false;
	fileTypeError = false;
	abortController: AbortController;
	imageWidth: number;
	imageHeight: number;
	sampleStatus: boolean;
	sampleType: string;
	sampleIndex: number;
	sampleSavedSettings;
	form: FormGroup<PhotoForm> = this.fb.group({
		photo: new FormControl<any>(null),
		photo_base64: new FormControl<string | null>(null),
		photo_src: new FormControl<string | null>(null),
		photo_blob: new FormControl<Blob | null>(null),
		cropped_src: new FormControl<string | null>(null),
		night_src: new FormControl<string | null>(null),
	});

	originalImageBase64: string | ArrayBuffer;
	originalCropperSettings: CropperSettings;

	cropper: Cropper;
	cropperBaseZoomRatio: number;
	cropperAspectRatio = 16 / 9;
	cropperCanvasOptions: Cropper.GetCroppedCanvasOptions = {
		maxWidth: 3072,
		maxHeight: 3072,
	};
	cropperConfig: Cropper.Options = {
		viewMode: 0,
		restore: false,
		dragMode: 'move',
		movable: true,
		cropBoxMovable: false,
		toggleDragModeOnDblclick: false,
	};
	readonly CropperAction = CropperAction;

	projectSamples: ProjectSamplesImages;

	slideConfig = {
		slidesToShow: 3,
		slidesToScroll: 3,
		dots: false,
		infinite: false,
		variableWidth: true,
		mobileFirst: false,
		arrows: false,
		responsive: [
			{
				breakpoint: CONFIG.breakpoints['phone'],
				settings: {
					slidesToShow: 1.5,
					slidesToScroll: 1,
				},
			},
		],
	};

	progressBar = {
		id: '',
		progress: 0,
		chars_received: null,
		content_length: null,
	};

	@HostListener('window:orientationchange', ['$event']) onOrientationChange() {
		if (this.selectedBackgroundType === 'project_sample' && this.form.value.photo_base64) {
			this.onSelectProjectSample(null, this.sampleType, this.sampleIndex);
		}
	}

	@HostListener('window:resize', ['$event']) onResize() {
		this.setImageCropperAspectRatio();
		setTimeout(this.setImageCropperToImageSize.bind(this));
	}

	constructor(
		private fb: UntypedFormBuilder,
		private engineService: EngineService,
		private shareService: ShareService,
		private setupPageService: SetupPageService,
		private sessionStorageService: SessionStorageService,
		private passDataService: PassDataService,
		private modalService: ModalService,
		private matDialog: MatDialog,
		private indexedDBService: IndexedDBService,
		public dialogRef: MatDialogRef<SelectBackgroundModalComponent>,
		private detectionService: DetectionService,

		@Inject(MAT_DIALOG_DATA) public data: { blind_detection: boolean },
	) {}

	ngOnInit() {
		this.onResize();
		this.engineService.setGizmoControlVisible(false);
		this.modalService.liftUpModal(this.dialogRef);
		this.backgroundImagesHandler();
		this.getCurrentBlindData();
	}

	ngOnDestroy() {
		this.engineService.setGizmoControlVisible(false);
	}

	getCurrentBlindData(): void {
		this.getProductType = this.setupPageService.getProductType();
		this.getBlindData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		if (!this.getBlindData) {
			this.dialogRef.disableClose = true;
			this.getProductType = this.setupPageService.getProductType();

			this.onSelectProductType(this.getProductType === 'all' ? 'outdoor' : this.getProductType);
			this.onSelectType('project_sample');
			this.onSelectProjectSample(null, this.selectedProductType, 1);
		} else if (this.shareService.getSelectOpeningModeValue()) {
			this.onSelectType('upload');
		}
	}

	backgroundImagesHandler(): void {
		const storageConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config);

		if (storageConfig) {
			this.projectSamples = { ...storageConfig?.iv_images };
		}
	}

	onSelectProductType(type: string) {
		this.selectedProductType = type;

		if (this.selectedBackgroundType === 'project_sample') {
			this.onSelectProjectSample(null, this.selectedProductType, 1);
		}
	}

	onSelectType(type: string) {
		const getProductType = this.setupPageService.getProductType();
		const screenType = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type);
		this.selectedBackgroundType = type;
		this.scrollToForm();
		this.onDeleteFile();

		this.isImageVisualisation = screenType === VIEW_TYPES.image_visualisation && getProductType === 'all';

		if (this.selectedBackgroundType === 'project_sample') {
			this.removeImageCropper();
		}

		if (this.selectedBackgroundType === 'upload') {
			this.abortController?.abort();
			this.setUploadedBackground();
		} else if (this.selectedBackgroundType === 'project_sample' && this.sampleType && this.sampleIndex) {
			this.onSelectProjectSample(null, this.sampleType, this.sampleIndex);
		}

		this.handleBackgroundTypeChange();
	}

	onSelectProjectSample(event: Event, type: string, index: number) {
		const target = event ? (event.currentTarget as HTMLElement) : null;

		this.selectedProductType = type;
		this.sampleStatus = true;
		this.sampleType = type;
		this.sampleIndex = index;

		this.form.controls.photo.reset();
		this.form.controls.photo_base64.reset();
		this.form.controls.photo_src.reset();

		if (target) {
			const sampleList = document.querySelectorAll(SELECTORS.background_project_samples);
			sampleList.forEach((element: HTMLElement) => element.classList.remove('active'));

			target.classList.add('active');
		}

		this.abortController?.abort();
		this.abortController = new AbortController();
		const signal = this.abortController.signal;
		const orientation = this.setupPageService.getScreenOrientation();
		const id = `${type}_${index}_${orientation}`;

		this.indexedDBService.getImage(STORAGE_NAMES.zip_image_visualisation_background, id).then((image) => {
			if (image) {
				this.form.controls.night_src.setValue(image.night_src);
				this.uploadFile(image.blob);
			} else {
				this.getSelectProjectImages(type, index, orientation, signal);
			}

			this.slickModal.get(Number(type === 'interior')).slickGoTo(index);
			this.scrollToElement(this.formElement.nativeElement.querySelector('button[type=submit]'));
		});
	}

	getSelectProjectImages(type: string, index: number, orientation: string, signal: AbortSignal): void {
		this.isLoading = true;

		const isOutdoor = type === 'outdoor';
		const selectProjectPromises = [
			this.getImage(signal, this.projectSamples[type].day[orientation][index]),
			isOutdoor && this.getImage(signal, this.projectSamples[type].night[orientation][index]),
		];

		this.clearProgressBar();

		Promise.all(selectProjectPromises).then(([dayBlob, nightBlob]) => {
			this.form.controls.night_src.setValue(nightBlob ? window.URL.createObjectURL(nightBlob) : null);
			this.uploadFile(dayBlob);
		});
	}

	async getImage(signal: AbortSignal, url: string): Promise<Blob> {
		const response = await fetch(url, { signal });
		const reader = response.body.getReader();
		const chunks = [];
		this.progressBar.content_length += +response.headers.get('content-length');

		const process = ({ done, value }) => {
			if (done) {
				this.clearProgressBar();
				return;
			}

			this.progressBar = {
				...this.progressBar,
				chars_received: this.progressBar.chars_received + value.length,
				id: `${this.sampleType}_${this.sampleIndex}`,
				progress: Math.round((this.progressBar.chars_received / this.progressBar.content_length) * 100),
			};
			chunks.push(value);

			return reader.read().then(process.bind(this));
		};

		await reader.read().then(process.bind(this));

		return new Blob(chunks);
	}

	clearProgressBar(): void {
		this.progressBar = {
			id: '',
			progress: 0,
			chars_received: null,
			content_length: null,
		};
	}

	createImageCropper(cropperSettings?: CropperSettings): void {
		if (this.form.value.photo_base64 && this.selectedBackgroundType === 'upload') {
			const image = document.getElementById(SELECTORS.cropper_image) as HTMLImageElement;

			const ready = () => {
				setTimeout(this.scrollToElement.bind(this, this.formElement.nativeElement.querySelector('button[type]')));

				const image = this.cropper.getImageData();
				this.cropperBaseZoomRatio = image.width / image.naturalWidth;

				if (!cropperSettings) {
					this.setImageCropperToImageSize();
					this.resetFormPristineOnCropChange();
					return;
				}

				const containerData = this.cropper.getContainerData();
				const { cropper_container_data, cropper_data, cropper_box_data, cropper_canvas_data } = cropperSettings;

				if (_.isEqual(containerData, cropper_container_data)) {
					this.cropper.setData(cropper_data).setCropBoxData(cropper_box_data).setCanvasData(cropper_canvas_data);
				} else {
					this.setImageCropperToImageSize();
					this.form.markAsDirty();
				}
			};

			const zoom = (e: Cropper.ZoomEvent) => {
				const zoomRatio = e.detail.ratio;
				const zoomDifference = this.cropperBaseZoomRatio / zoomRatio;

				if (zoomDifference > 2 || zoomDifference < 0.2) {
					e.preventDefault();
				} else {
					this.form.markAsDirty();
				}
			};

			const cropend = (event: CropEndEvent) => {
				if (!['all', 'none'].includes(event.detail.action as Cropper.Action | 'none')) {
					this.form.markAsDirty();
					this.resetFormPristineOnCropChange();
				}

				this.centerCropBoxAndImage();
			};

			this.cropper = new Cropper(image, { ...this.cropperConfig, ready, zoom, cropend });
		}
	}

	centerCropBoxAndImage(): void {
		const containerData = this.cropper.getContainerData();
		const canvasData = this.cropper.getCanvasData();
		const imageData = this.cropper.getImageData();
		const cropBoxData = this.cropper.getCropBoxData();

		// CENTER CROPBOX LOGIC
		// calculate the center of the crop box
		const cropBoxCenterX = cropBoxData.left + cropBoxData.width / 2;
		const cropBoxCenterY = cropBoxData.top + cropBoxData.height / 2;

		// calculate the center of the container
		const containerCenterX = containerData.width / 2;
		const containerCenterY = containerData.height / 2;

		// calculate the difference between the centers
		const deltaX = containerCenterX - cropBoxCenterX;
		const deltaY = containerCenterY - cropBoxCenterY;

		// move the crop box by the difference
		this.cropper.setCropBoxData({
			left: cropBoxData.left + deltaX,
			top: cropBoxData.top + deltaY,
		});

		// CENTER CROPED IMAGE LOGIC
		// calculate the difference between the width of container and image
		const imageDeltaX = containerData.width - imageData.width;
		const imageDeltaY = containerData.height - imageData.height;

		// calculate the left and top position of the cropped area
		const croppedLeft = canvasData.left + cropBoxData.left - canvasData.width / 2 + cropBoxData.width / 2;
		const croppedTop = canvasData.top + cropBoxData.top - canvasData.height / 2 + cropBoxData.height / 2;

		// calculate the left and top position of the cropped image
		const imgLeft = canvasData.left - croppedLeft + imageDeltaX / 2 - imageData.left;
		const imgTop = canvasData.top - croppedTop + imageDeltaY / 2 - imageData.top;

		this.cropper.setCanvasData({
			left: canvasData.left + imgLeft,
			top: canvasData.top + imgTop,
		});
	}

	setImageCropperAspectRatio(): void {
		const wrapper = document.querySelector(SELECTORS.wrapper);
		this.cropperAspectRatio = wrapper.clientWidth / wrapper.clientHeight;
	}

	setImageCropperToImageSize(): void {
		if (this.cropper) {
			const canvasData = this.cropper.getCanvasData();
			this.cropper.setCropBoxData({ height: canvasData.height, width: canvasData.width, top: canvasData.top, left: canvasData.left });
		}
	}

	removeImageCropper(): void {
		this.form.controls.cropped_src.reset();
		this.cropper?.destroy();
		this.cropper = null;
	}

	onImageCropperButton(type: CropperAction): void {
		switch (type) {
			case CropperAction.FLIP:
				const { rotate = 0, scaleY = 1, scaleX = 1 } = this.cropper.getImageData();

				if (rotate === 90 || rotate === 270) {
					this.cropper.scaleY(-scaleY);
				} else {
					this.cropper.scaleX(-scaleX);
				}

				break;
			case CropperAction.ROTATE:
				this.cropper.rotate(90);
				break;
			case CropperAction.ZOOM_IN:
				this.cropper.zoom(0.1);
				break;
			case CropperAction.ZOOM_OUT:
				this.cropper.zoom(-0.1);
				break;
			case CropperAction.RESET:
				this.cropper.reset();
				this.setImageCropperToImageSize();
				break;
			case CropperAction.DELETE:
				this.onDeleteFile();
				break;
		}

		this.form.markAsDirty();
		this.resetFormPristineOnCropChange();
	}

	onSelectFile(event: Event) {
		const input = event.target as HTMLInputElement;
		const fileArray = this.convertFileList(input.files);

		if (fileArray.length) {
			this.sampleType = null;
			this.sampleIndex = null;
			this.uploadFileToPromise(fileArray[0]).then(() => this.createImageCropper());
		}

		if (input.id === 'photo') {
			this.scrollToForm();
		}
	}

	onDropFile(event) {
		this.preventDefaultAndStopPropagation(event);
		event.target.classList.remove('drag-over');

		const fileArray = this.convertFileList(event.dataTransfer.files);

		if (fileArray.length) {
			this.uploadFileToPromise(fileArray[0]).then(() => this.createImageCropper());
		}
	}

	setUploadedBackground() {
		const ivBackgroundImage = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_uploaded_image_visualisation_background);

		if (ivBackgroundImage) {
			this.indexedDBService.getImage(STORAGE_NAMES.zip_image_visualisation_background, ivBackgroundImage.id).then((image) => {
				this.sampleType = null;
				this.sampleIndex = null;
				this.sampleSavedSettings = {
					settings: ivBackgroundImage.settings,
					shutterValues: ivBackgroundImage.shutterValues,
				};

				if (image) {
					this.uploadFileToPromise(image.blob).then(() => {
						this.originalImageBase64 = this.form.value.photo_base64;
						this.originalCropperSettings = image.cropper_settings;

						this.createImageCropper(image.cropper_settings);
					});
				}
			});
		}
	}

	onDragEvent(event: Event) {
		this.preventDefaultAndStopPropagation(event);

		if (event.type === 'dragover') {
			(event.target as HTMLElement).classList.add('drag-over');
		} else if (event.type === 'dragleave') {
			(event.target as HTMLElement).classList.remove('drag-over');
		}

		this.form.markAsDirty();
	}

	preventDefaultAndStopPropagation(event: Event) {
		event.preventDefault();
		event.stopPropagation();
	}

	convertFileList(files: FileList): File[] {
		this.fileTypeError = false;
		this.maxSizeError = false;

		return [...files].filter((file: File) => {
			const isAllowedType = this.allowedMimeType.some((type: string) => type === file.type);
			const isCorrectFileSize = file.size <= this.maxFileSize;

			if (!isAllowedType) {
				this.fileTypeError = true;
			} else if (!isCorrectFileSize) {
				this.maxSizeError = true;
			}

			return isAllowedType && isCorrectFileSize;
		});
	}

	onLoadImage(file: File | Blob): Promise<LoadedImageData> {
		return new Promise((resolve, reject) => {
			const image = new Image();
			image.src = window.URL.createObjectURL(file);
			image.onload = () => {
				resolve({
					src: image.src,
					height: image.height,
					width: image.width,
				});
			};
			image.onerror = (error) => reject(error);
		});
	}

	uploadFile(file: File | Blob, resolve?: () => void) {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			this.isLoading = false;
			this.form.controls.photo_base64.setValue(reader.result);
		};

		if (resolve) {
			reader.onloadend = () => resolve();
		}

		this.onLoadImage(file).then((loadedImage) => {
			this.form.controls.photo_src.setValue(loadedImage.src);
			this.form.controls.photo_blob.setValue(file);
			this.imageHeight = loadedImage.height;
			this.imageWidth = loadedImage.width;
		});
	}

	uploadFileToPromise(file: File | Blob) {
		return new Promise<void>((resolve) => this.uploadFile(file, resolve));
	}

	onDeleteFile(type?: string) {
		this.form.controls.photo.reset();
		this.form.controls.photo_base64.reset();
		this.form.controls.photo_src.reset();
		this.fileTypeError = false;
		this.maxSizeError = false;

		if (type === 'photo') {
			this.selectedBackgroundType = '';
			this.sessionStorageService.removeBlindData(STORAGE_NAMES.zip_iv_type);
		}
	}

	onSubmit() {
		if (!this.form.valid) {
			return;
		}

		this.sessionStorageService.setSession(this.selectedBackgroundType, STORAGE_NAMES.zip_iv_type);
		this.isLoading = true;
		this.isSubmitting = true;

		if (this.cropper) {
			this.cropper.getCroppedCanvas(this.cropperCanvasOptions).toBlob((file) => {
				if (this.isImageUnchanged() && !this.getBlindData?.length) {
					this.onSubmitCallback(this.setBackgroundConfig.bind(this));
					return;
				}

				// Only detect image if it was uploaded
				if (this.imageDetectionStatus()) {
					void this.detectionService.startDetection(file);
				}

				this.onLoadImage(file).then((loadedImage) => {
					this.form.controls.cropped_src.setValue(loadedImage.src);
					this.imageHeight = loadedImage.height;
					this.imageWidth = loadedImage.width;
					this.onSubmitCallback(this.setBackgroundConfig.bind(this));
				});
			});
		} else {
			if (this.selectedBackgroundType === 'upload') {
				void this.detectionService.startDetection(this.form.value.photo_blob);
			}

			this.onSubmitCallback(this.setBackgroundConfig.bind(this));
		}
	}

	setBackgroundConfig(): void {
		const backgroundImage = this.getBackgroundConfig();

		if (!this.sampleType) {
			this.sampleSavedSettings = this.isImageUnchanged() ? this.sampleSavedSettings : {};
			backgroundImage['settings'] = this.sampleSavedSettings.settings;
			backgroundImage['shutterValues'] = this.sampleSavedSettings.shutterValues;
		}

		// onClose() is called first to avoid incorrect display of Quiz Popup
		this.onClose(this.imageDetectionStatus());
		this.closeRotateDeviceModal();
		this.engineService.setBackgroundImage(backgroundImage);

		this.passDataService.isLiveOpened = false;
	}

	getBackgroundConfig(): BackgroundConfig {
		const orientation = this.setupPageService.getScreenOrientation();
		const imageOrientation = this.imageWidth > this.imageHeight ? 'landscape' : 'portrait';
		const cropperSettings = this.getCropperSettings();

		return {
			src: this.form.value.photo_src,
			night_src: this.form.value.night_src,
			blob: this.form.value.photo_blob,
			base64: this.form.value.photo_base64,
			cropped_src: this.form.value.cropped_src,
			cropper_settings: cropperSettings,
			width: this.imageWidth,
			height: this.imageHeight,
			theme: IV_THEME_MODES.day,
			type: this.sampleType,
			index: this.sampleIndex,
			image_orientation: this.sampleType ? orientation : imageOrientation,
			id: this.sampleType ? `${this.sampleType}_${this.sampleIndex}_${orientation}` : 'own_image',
		};
	}

	getCropperSettings(): CropperSettings {
		return {
			cropper_data: this.cropper?.getData(),
			cropper_box_data: this.cropper?.getCropBoxData(),
			cropper_canvas_data: this.cropper?.getCanvasData(),
			cropper_container_data: this.cropper?.getContainerData(),
		};
	}

	imageDetectionStatus(): boolean {
		return IMAGE_DETECTOR === 'off'
			? false
			: this.selectedBackgroundType === 'upload' && (!this.isImageUnchanged() || !this.getBlindData?.length);
	}

	isImageUnchanged(): boolean {
		const cropperSettings = this.getCropperSettings();

		return this.originalImageBase64 === this.form.value.photo_base64 && _.isEqual(this.originalCropperSettings, cropperSettings);
	}

	resetFormPristineOnCropChange(): void {
		if (this.isImageUnchanged() && !this.engineService.sampleImage.type) {
			this.form.markAsPristine();
		}
	}

	onBlindTypeSelect(type: string): void {
		const id = Math.floor(1000 + Math.random() * 9000);

		this.shareService.getModelLoaded.pipe(first()).subscribe(this.engineService.createScene.bind(this.engineService, type, id.toString()));

		this.sessionStorageService.setSession(type, STORAGE_NAMES.zip_blind_type);
		this.shareService.setBlindType({ type, id });
	}

	onClose(selectOpeningMode: boolean = null): void {
		if (selectOpeningMode) {
			this.shareService.setSelectOpeningMode(true);
			this.engineService.toggleAllRootMeshes(false);
		} else if (selectOpeningMode === false) {
			this.shareService.setSelectOpeningMode(false);
			this.engineService.toggleAllRootMeshes(true);
		}
		this.dialogRef.close();
	}

	scrollToForm() {
		if (
			this.formElement &&
			((window.orientation === 0 && window.innerWidth <= CONFIG.breakpoints['phone']) ||
				(window.orientation === 90 && window.innerWidth <= CONFIG.breakpoints['tablet-landscape']))
		) {
			setTimeout(this.scrollToElement.bind(this, this.formElement.nativeElement));
		}
	}

	trackByIndex(index: number): number {
		return index;
	}

	openLive(): void {
		const dialog = this.matDialog.open(ArLoadingModalComponent, this.modalService.getConfig(ArLoadingModalComponent.ID));
		const addVideoLayer = () => {
			this.engineService
				.addVideoLayer()
				.then(() => {
					this.onClose(false);
					dialog.close();
				})
				.catch(() => {
					this.getBlindData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
					this.engineService.videoLayer.dispose();
					dialog.close();
				});
		};

		this.onSubmitCallback(addVideoLayer);
	}

	onSubmitCallback(callback: () => void): void {
		if (!this.getBlindData && this.selectedProductType) {
			this.onBlindTypeSelect(this.selectedProductType);

			const sizeChangedSubscribe = this.shareService.getBlindSizeChanged.subscribe(() => {
				sizeChangedSubscribe.unsubscribe();
				callback();
			});
		} else {
			callback();
		}
	}

	closeRotateDeviceModal(): void {
		const dialog = this.matDialog.getDialogById(RotateDeviceModalComponent.ID);

		if (dialog) {
			dialog.close();
		}
	}

	scrollToElement(element: HTMLElement): void {
		element.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	}

	handleBackgroundTypeChange(): void {
		const prevBackgroundType = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_iv_type);

		if (this.selectedBackgroundType !== prevBackgroundType) {
			this.form.markAsDirty();
		}
	}
}
